import {
  Outlet,
  Link,
  createRootRoute,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
// Inlined CSS (prod only): the stylesheet is the page's single render-blocking
// request, so in production we embed it in a <style> tag instead of a <link>.
// Same bytes over the wire (HTML is compressed), one less round-trip before
// first paint. Dev keeps the ?url <link> so Vite/Lovable CSS HMR still works.
import appCssInline from "../styles.css?inline";
// The latin subset of the Inter variable font covers da/en/de text and is the
// last hop of the critical request chain — preloading it starts the download
// with the HTML instead of after style/layout. Same hashed asset the CSS
// @font-face points at, so the preload is a guaranteed cache hit.
import interLatinWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { htmlLang, localeFromPathname } from "@/i18n/config";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import { DEFAULT_OG_IMAGE, ldScript, organization, website } from "@/lib/schema";

// Minified inline tracker for the first-party analytics (kept dependency-free).
// `f` guards so the original external referrer is only attributed to the first
// pageview; SPA navigations report an empty referrer. `lp` dedupes repeated
// sends for the same path (replaceState/scroll restoration).
const ANALYTICS_SNIPPET = `(function(){if(/^(www\\.)?misupport\\.dk$/.test(location.hostname)===false)return;var lp=null,f=true;function s(){var p=location.pathname+location.search;if(p===lp||p.indexOf("/analytics")===0)return;lp=p;var d=JSON.stringify({p:p,r:f?document.referrer:"",w:innerWidth,l:navigator.language});f=false;if(navigator.sendBeacon){navigator.sendBeacon("/api/event",new Blob([d],{type:"application/json"}))}else{fetch("/api/event",{method:"POST",body:d,keepalive:true,headers:{"Content-Type":"application/json"}})}}var h=history,ps=h.pushState;h.pushState=function(){ps.apply(h,arguments);setTimeout(s,0)};addEventListener("popstate",s);s()})();`;

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "MI Support - Bygge-, projekt- og sikkerhedsledelse" },
      { name: "description", content: "MI Support leverer erfarne bygge-, projekt- og sikkerhedsledere (HSE) til større og komplekse byggeprojekter." },
      { name: "author", content: "MI Support" },
      { property: "og:site_name", content: "MI Support" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "MI Support" },
      { property: "og:description", content: "Bygge-, projekt- og sikkerhedsledelse til større og komplekse byggeprojekter." },
      { property: "og:image", content: DEFAULT_OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: DEFAULT_OG_IMAGE },
    ],
    links: [
      // Inter is self-hosted via @fontsource-variable/inter (imported in
      // styles.css), so no external Google Fonts request is needed.
      // In production the CSS is inlined via `styles` below instead of linked.
      ...(import.meta.env.PROD ? [] : [{ rel: "stylesheet", href: appCss }]),
      // Font preload: crossOrigin is required for font preloads (even
      // same-origin) or the browser refetches the file at @font-face time.
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: interLatinWoff2,
        crossOrigin: "anonymous",
      },
      // GTM is the first third-party on every page; preconnect saves the
      // DNS+TLS handshake before the async gtm.js request fires.
      { rel: "preconnect", href: "https://www.googletagmanager.com" },
      // Favicons: the MI logomark in brand blue (#215888). favicon.ico serves
      // legacy /favicon.ico requests; the SVG wins in modern browsers.
      { rel: "icon", href: "/favicon.ico", sizes: "48x48" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
    ],
    styles: import.meta.env.PROD ? [{ children: appCssInline }] : [],
    // Site-wide structured data — present on every page so Google has a single,
    // canonical Organization + WebSite entity to anchor the rest of the graph.
    // In production the cookieless first-party analytics tracker follows: it
    // beacons {path, referrer, width, lang} to /api/event on load and on SPA
    // navigations (pushState/popstate). No cookies, no localStorage, no IDs —
    // visitor uniqueness is derived server-side (see src/routes/api/event.ts).
    scripts: [
      ldScript(organization()),
      ldScript(website()),
      ...(import.meta.env.PROD ? [{ children: ANALYTICS_SNIPPET }] : []),
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = htmlLang[localeFromPathname(pathname)];
  return (
    <html lang={lang}>
      <head>
        {/* Google Tag Manager — placed as high in <head> as possible */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PRN6MQPJ');`,
          }}
        />
        {/* End Google Tag Manager */}
        <HeadContent />
      </head>
      <body>
        {/* Google Tag Manager (noscript) — immediately after <body> */}
        <noscript>
          <iframe
            src="https://www.googletagmanager.com/ns.html?id=GTM-PRN6MQPJ"
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            title="Google Tag Manager"
          />
        </noscript>
        {/* End Google Tag Manager (noscript) */}
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <LocaleProvider>
      <Outlet />
    </LocaleProvider>
  );
}
